import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { Plus, Pencil, Trash2, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import LoadingSpinner from '../../components/LoadingSpinner';

const AdminEventos = () => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvento, setEditingEvento] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '', descripcion: '', fecha: '', ubicacion: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchEventos();
  }, []);

  const fetchEventos = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'eventos'));
      setEventos(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha?.toDate()
      })));
    } catch (error) {
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let imagen = editingEvento?.imagen || '';
      if (imageFile) {
        const imageRef = ref(storage, `eventos/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imagen = await getDownloadURL(imageRef);
      }

      const eventoData = {
        titulo: formData.titulo,
        descripcion: formData.descripcion,
        fecha: Timestamp.fromDate(new Date(formData.fecha)),
        ubicacion: formData.ubicacion,
        imagen
      };

      if (editingEvento) {
        await updateDoc(doc(db, 'eventos', editingEvento.id), eventoData);
        toast.success('Evento actualizado');
      } else {
        await addDoc(collection(db, 'eventos'), eventoData);
        toast.success('Evento creado');
      }

      setDialogOpen(false);
      resetForm();
      fetchEventos();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (evento) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await deleteDoc(doc(db, 'eventos', evento.id));
      toast.success('Evento eliminado');
      fetchEventos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (evento) => {
    setEditingEvento(evento);
    setFormData({
      titulo: evento.titulo,
      descripcion: evento.descripcion,
      fecha: format(evento.fecha, "yyyy-MM-dd'T'HH:mm"),
      ubicacion: evento.ubicacion || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEvento(null);
    setFormData({ titulo: '', descripcion: '', fecha: '', ubicacion: '' });
    setImageFile(null);
  };

  if (loading) return <LoadingSpinner fullScreen />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold">Gestión de Eventos</h1>
          <p className="text-muted-foreground">Administra el calendario de eventos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2" size={18} />Nuevo Evento</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingEvento ? 'Editar' : 'Nuevo'} Evento</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input value={formData.titulo} onChange={(e) => setFormData({ ...formData, titulo: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Textarea value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} required rows={4} />
              </div>
              <div className="space-y-2">
                <Label>Fecha y Hora *</Label>
                <Input type="datetime-local" value={formData.fecha} onChange={(e) => setFormData({ ...formData, fecha: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Input value={formData.ubicacion} onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })} placeholder="Plaza Central" />
              </div>
              <div className="space-y-2">
                <Label>Imagen {!editingEvento && '*'}</Label>
                <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} required={!editingEvento} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancelar</Button>
                <Button type="submit" disabled={uploading}>{uploading ? 'Guardando...' : 'Guardar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {eventos.map((evento) => (
          <Card key={evento.id}>
            <div className="relative h-48">
              <img src={evento.imagen} alt={evento.titulo} className="w-full h-full object-cover" />
            </div>
            <CardContent className="p-4">
              <h3 className="font-heading font-semibold mb-2">{evento.titulo}</h3>
              <p className="text-sm text-muted-foreground mb-3">{evento.descripcion.substring(0, 80)}...</p>
              <div className="flex items-center text-xs text-muted-foreground mb-4">
                <CalendarIcon size={14} className="mr-1" />
                {format(evento.fecha, "dd/MM/yyyy HH:mm")}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(evento)} className="flex-1">
                  <Pencil size={14} className="mr-1" />Editar
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(evento)} className="flex-1">
                  <Trash2 size={14} className="mr-1" />Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminEventos;
